import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { articlesList } from '@/data/articlesData';

interface ArticleRecommendationProps {
  title?: string;
  subtitle?: string;
  count?: number;
  slugs?: string[];
  variant?: 'section' | 'compact';
}

const ArticleRecommendation = ({
  title = '延伸閱讀',
  subtitle,
  count = 3,
  slugs,
  variant = 'section',
}: ArticleRecommendationProps) => {
  const articles = slugs
    ? slugs.map(s => articlesList.find(a => a.slug === s)).filter(Boolean).slice(0, count)
    : articlesList.slice(0, count);

  if (articles.length === 0) return null;

  if (variant === 'compact') {
    return (
      <div className="space-y-3">
        <h4 className="font-sans-tc text-sm uppercase tracking-widest text-brand-charcoal">{title}</h4>
        <ul className="list-stacked">
          {articles.map(article => (
            <li key={article!.id}>
              <Link
                to={`/articles/${article!.slug}`}
                className="text-brand-mist text-sm hover:text-brand-rose transition-colors line-clamp-1 flex items-center gap-1"
              >
                <ArrowRight className="h-3 w-3 shrink-0" />
                {article!.title}
              </Link>
            </li>
          ))}
        </ul>
        <Link
          to="/articles"
          className="text-brand-rose text-sm hover:underline inline-flex items-center gap-1 mt-2"
        >
          查看全部文章 <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-brand-sand">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-serif-tc text-3xl lg:text-4xl font-light mb-3 text-brand-charcoal tracking-wide">{title}</h2>
          {subtitle && <p className="font-sans-tc text-brand-mist">{subtitle}</p>}
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {articles.map((article, index) => (
            <motion.div
              key={article!.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <Link
                to={`/articles/${article!.slug}`}
                className="block p-6 rounded-2xl bg-brand-white shadow-soft hover:shadow-elevated transition-all duration-300 h-full group"
              >
                <span className="font-sans-tc text-xs text-brand-rose font-medium uppercase tracking-wider">
                  {article!.category}
                </span>
                <h3 className="font-serif-tc text-lg mt-2 mb-3 line-clamp-2 text-brand-charcoal group-hover:text-brand-rose transition-colors">
                  {article!.title}
                </h3>
                <p className="font-sans-tc text-brand-mist text-sm line-clamp-2 mb-4">
                  {article!.summary}
                </p>
                <span className="font-sans-tc text-brand-rose text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  閱讀完整文章 <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/articles"
            className="font-sans-tc inline-flex items-center gap-2 px-8 py-3 rounded-full border border-brand-rose text-brand-rose hover:bg-brand-rose hover:text-white transition-colors text-sm"
          >
            查看全部保養文章 <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ArticleRecommendation;
